/* quick-view.js — Standalone quick-view for TBP
 * Uses /products/handle.js product JSON (no section-overlay needed).
 * Requires pbm-quick-view-base.js (DrawerComponent) to be loaded first.
 */

if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends DrawerComponent {
      constructor() {
        super();
        this._loaded = false;
      }

      get requiresBodyAppended() { return true; }

      prepareToShow() {
        super.prepareToShow();
        if (!this._loaded) this._fetchAndRender();
      }

      handleAfterHide() {
        super.handleAfterHide();
        const body = this.querySelector('.quick-view__content');
        if (body) body.innerHTML = '';
        this.classList.remove('quick-view-loaded');
        this._loaded = false;
      }

      /* ── Utilities ─────────────────────────────────────────────── */
      _jsonUrl() {
        return (this.dataset.productUrl || '').split('?')[0] + '.js';
      }

      _src(img) {
        if (!img) return '';
        return typeof img === 'string' ? img : (img.src || '');
      }

      _resize(img, px) {
        const url = this._src(img);
        if (!url) return '';
        return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, `_${px}x.$1$2`);
      }

      _money(cents) {
        /* Return empty for null/undefined/zero — don't show "0.000 KWD" */
        if (!cents) return '';
        const isAr  = window.qvCurrencyIsAr
                   ?? (document.documentElement.lang === 'ar'
                       || document.documentElement.dir === 'rtl');
        const label = isAr ? 'د.ك' : 'KWD';

        /* Use Shopify.formatMoney to get the correct numeric amount —
           it already knows how to divide by the store's subunit correctly.
           We then strip the store's own symbol and re-label with KWD / د.ك */
        let numStr;
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const raw = Shopify.formatMoney(cents);
          /* Extract digits, dots, commas — discards existing currency label */
          numStr = raw.replace(/[^\d.,]/g, '').trim();
          /* Pad/trim to exactly 3 decimal places for KWD */
          const float = parseFloat(numStr.replace(/,/g, '')) || 0;
          numStr = float.toFixed(3);
        } else {
          /* Fallback: Shopify stores prices in subunits (cents for AED = ÷100) */
          numStr = (cents / 100).toFixed(3);
        }

        /* Remove .000 if no decimals, keep other decimals like .50 */
        numStr = numStr.replace(/\.000$/, '');

        return `${numStr} ${label}`;
      }

      _esc(s) {
        return String(s || '').replace(/[&<>"']/g,
          c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
      }

      _buildOptions(p) {
        return (p.options || []).map((opt, i) => {
          if (opt && typeof opt === 'object') {
            const values = opt.values && opt.values.length
              ? opt.values : this._variantValues(p, i);
            return { name: opt.name || `Option ${i + 1}`, values };
          }
          return { name: String(opt), values: this._variantValues(p, i) };
        });
      }

      _variantValues(p, idx) {
        const key  = `option${idx + 1}`;
        const seen = new Set();
        return (p.variants || [])
          .map(v => v[key])
          .filter(v => v != null && v !== '' && !seen.has(v) && seen.add(v));
      }

      _isColorOpt(name) {
        const trigger = (window.qvSwatchTrigger || 'color,colour,farbe,couleur,لون').toLowerCase();
        return trigger.split(',').some(t => t.trim() && (name || '').toLowerCase().includes(t.trim()));
      }

      _isSizeOpt(name) {
        const triggers = ['size', 'مقاس', 'مقاسات', 'قياس'];
        const n = (name || '').toLowerCase();
        return triggers.some(t => n === t || n.includes(t));
      }

      _swatchColor(val) {
        const list = window.qvSwatchList || {};
        return list[val.toLowerCase()] || list[val] || val.toLowerCase();
      }

      /* ── Fetch ─────────────────────────────────────────────────── */
      _fetchAndRender() {
        const body = this.querySelector('.quick-view__content');
        if (!body) return;

        fetch(this._jsonUrl())
          .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
          .then(p  => { this._loaded = true; this._render(p, body); })
          .catch(() => {
            if (body) body.innerHTML = '<p class="qv-error">Could not load product.</p>';
            this.classList.add('quick-view-loaded');
          });
      }

      /* ── Render ─────────────────────────────────────────────────── */
      _render(p, body) {
        const v       = p.variants && p.variants[0];
        const price   = v ? this._money(v.price) : '';
        /* Only show compare price if it's a real positive value greater than the sale price */
        const compare = v
          && v.compare_at_price
          && v.compare_at_price > 0
          && v.compare_at_price > v.price
            ? this._money(v.compare_at_price) : '';

        const imgs = (p.images && p.images.length)
          ? p.images
          : (p.featured_image ? [p.featured_image] : []);

        /* Inventory / stock display — matches product page component */
        const dotSvg = `<svg class="icon icon-inventory-status" width="15" height="15" viewBox="0 0 15 15"><circle cx="7.5" cy="7.5" r="7.5" fill="currentColor"/><circle cx="7.5" cy="7.5" r="5" fill="currentColor" stroke="#FFF"/></svg>`;
        const qty    = v ? (v.inventory_quantity || 0) : 0;
        const mgmt   = v ? v.inventory_management : null;
        const policy = v ? v.inventory_policy : null;
        const available = v ? v.available : true;
        const _arStock  = this._isAr();
        let stockHtml = '';
        {
          let color, label;
          if (!available) {
            color = 'rgb(200,200,200)';
            label = _arStock ? 'غير متوفر' : 'Out of stock';
          } else if (mgmt === 'shopify' && qty > 0 && qty <= 10) {
            color = 'rgb(238,148,65)';
            label = _arStock ? `كمية محدودة: ${qty} متبقٍ` : `Low stock: ${qty} left`;
          } else {
            color = 'rgb(62,214,96)';
            label = _arStock ? 'متوفر' : 'In stock';
          }
          stockHtml = `<p class="product__inventory caption-with-letter-spacing" role="status">
            <span class="svg-wrapper" style="color:${color}">${dotSvg}</span>${label}</p>`;
        }

        /* Shipping line */
        const shopUrl   = window.Shopify && Shopify.shop ? Shopify.shop : '';
        const _isArShip = document.documentElement.lang === 'ar' || document.documentElement.getAttribute('dir') === 'rtl';
        const shippingUrl = (_isArShip ? '/ar' : '') + '/policies/shipping-policy';
        const shippingHtml = _isArShip
          ? `<p class="qv-shipping"><a href="${shippingUrl}" class="qv-shipping__link">الشحن</a> يُحسب عند الدفع.</p>`
          : `<p class="qv-shipping"><a href="${shippingUrl}" class="qv-shipping__link">Shipping</a> calculated at checkout.</p>`;

        const galleryHtml = this._buildGallery(p, imgs);
        const optionsHtml = this._buildVariantHTML(p);

        body.innerHTML = `
          <div class="qv-layout">

            ${galleryHtml}

            <div class="qv-info">
              <h2 class="qv-title">${this._esc(p.title)}</h2>

              <div class="qv-price-row">
                <span class="qv-price${compare ? ' on-sale' : ''}">${price}</span>
                ${compare ? `<s class="qv-compare">${compare}</s>` : ''}
              </div>

              ${shippingHtml}
              ${stockHtml}


              ${optionsHtml}

              <form class="qv-form" id="qv-form-${p.id}" novalidate>
                <input type="hidden" name="id" class="qv-vid"
                       value="${v ? v.id : ''}">

                <div class="qv-form__buttons">
                  <div class="qv-qty-row">
                    <div class="qv-qty">
                      <button type="button" class="qv-qty__btn qv-qty__minus" aria-label="Decrease">−</button>
                      <input type="number" name="quantity" class="qv-qty__input"
                             value="1" min="1" aria-label="Quantity">
                      <button type="button" class="qv-qty__btn qv-qty__plus" aria-label="Increase">+</button>
                    </div>
                    <button type="submit" class="qv-atc" ${v && !v.available ? 'disabled' : ''}>
                      <span class="qv-atc__label">
                        ${v && v.available ? (this._isAr() ? 'أضف إلى السلة' : 'Add to Cart') : (this._isAr() ? 'غير متوفر' : 'Sold Out')}
                      </span>
                      <span class="qv-atc__spinner" hidden>
                        <svg width="18" height="18" viewBox="0 0 50 50"
                             style="animation:qv-spin 1s linear infinite;display:block">
                          <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor"
                                  stroke-width="4" stroke-dasharray="80" stroke-dashoffset="20"
                                  stroke-linecap="round"/>
                        </svg>
                      </span>
                    </button>
                  </div>
                  ${v && v.available ? `
                  <button type="button" class="qv-buy-now" data-product-url="${this._esc(p.url)}">
                    <span class="qv-buy-now__label">${this._isAr() ? 'اشتر الآن' : 'Buy it now'}</span>
                    <span class="qv-buy-now__spinner" hidden>
                      <svg width="18" height="18" viewBox="0 0 50 50"
                           style="animation:qv-spin 1s linear infinite;display:block">
                        <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor"
                                stroke-width="4" stroke-dasharray="80" stroke-dashoffset="20"
                                stroke-linecap="round"/>
                      </svg>
                    </span>
                  </button>` : ''}
                </div>
              </form>
            </div>

          </div>

          <!-- Sticky footer CTA -->
          <div class="qv-footer">
            <a href="${this._esc(p.url)}" class="qv-footer__link">
              ${this._isAr() ? 'عرض التفاصيل الكاملة' : 'View Full Details'}
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
                   stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                   stroke-linejoin="round">
                <path d="M4 16L16 4M16 4H8M16 4V12"/>
              </svg>
            </a>
          </div>
        `;

        this._bindGallery(body, imgs);
        this._bindVariants(p, body);
        this._bindQty(body);
        this._bindForm(body);
        this._bindBuyNow(body);

        setTimeout(() => this.classList.add('quick-view-loaded'), 300);
      }

      /* ── Gallery — peek carousel ────────────────────────────────── */
      _buildGallery(p, imgs) {
        if (!imgs.length) return '';

        const slides = imgs.map((img, i) => `
          <div class="qv-gallery__slide${i === 0 ? ' active' : ''}">
            <img src="${this._resize(img, 700)}"
                 srcset="${this._resize(img, 400)} 400w, ${this._resize(img, 700)} 700w"
                 sizes="80vw"
                 alt="${this._esc(this._src(img) && img.alt ? img.alt : p.title)}"
                 loading="${i === 0 ? 'eager' : 'lazy'}"
                 class="qv-gallery__img">
          </div>`).join('');

        const nav = imgs.length > 1 ? `
          <div class="qv-gallery__controls">
            <button class="qv-gallery__arrow qv-gallery__arrow--prev" type="button" aria-label="Previous image">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none"
                   stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12.5 15L7.5 10L12.5 5"/>
              </svg>
            </button>
            <span class="qv-gallery__counter">
              <span class="qv-gallery__current">1</span>/${imgs.length}
            </span>
            <button class="qv-gallery__arrow qv-gallery__arrow--next" type="button" aria-label="Next image">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none"
                   stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7.5 5L12.5 10L7.5 15"/>
              </svg>
            </button>
          </div>` : `
          <div class="qv-gallery__controls">
            <span class="qv-gallery__counter">1/1</span>
          </div>`;

        return `<div class="qv-gallery">
          <div class="qv-gallery__track">${slides}</div>
          ${nav}
        </div>`;
      }

      _bindGallery(body, imgs) {
        const track   = body.querySelector('.qv-gallery__track');
        const current = body.querySelector('.qv-gallery__current');
        const prev    = body.querySelector('.qv-gallery__arrow--prev');
        const next    = body.querySelector('.qv-gallery__arrow--next');
        if (!track) return;

        let idx = 0;

        const show = (i) => {
          idx = Math.max(0, Math.min(imgs.length - 1, i));
          track.style.transform = `translateX(calc(${idx} * (-88% - 0.8rem)))`;
          if (current) current.textContent = idx + 1;
          if (prev) prev.disabled = idx === 0;
          if (next) next.disabled = idx === imgs.length - 1;
        };

        if (prev) prev.addEventListener('click', () => show(idx - 1));
        if (next) next.addEventListener('click', () => show(idx + 1));

        /* Touch swipe */
        let startX = 0;
        track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
        track.addEventListener('touchend', e => {
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
        }, { passive: true });

        /* Mouse drag */
        let dragStartX = 0, dragging = false, dragMoved = false;
        const slideW = () => track.parentElement.offsetWidth * 0.88 + 8; // 88% + gap

        track.addEventListener('mousedown', e => {
          dragging = true; dragMoved = false;
          dragStartX = e.clientX;
          track.style.transition = 'none';
          track.style.cursor = 'grabbing';
        });

        window.addEventListener('mousemove', e => {
          if (!dragging) return;
          const dx = e.clientX - dragStartX;
          if (Math.abs(dx) > 4) dragMoved = true;
          track.style.transform = `translateX(calc(${idx} * (-88% - 0.8rem) + ${dx}px))`;
        });

        window.addEventListener('mouseup', e => {
          if (!dragging) return;
          dragging = false;
          track.style.transition = '';
          track.style.cursor = '';
          const dx = e.clientX - dragStartX;
          if (Math.abs(dx) > 50) show(dx < 0 ? idx + 1 : idx - 1);
          else show(idx);
        });

        /* Prevent accidental link/image drag */
        track.addEventListener('dragstart', e => e.preventDefault());
        track.addEventListener('click', e => { if (dragMoved) e.preventDefault(); }, true);

        show(0);
      }

      /* ── Variants ───────────────────────────────────────────────── */
      _isAr() {
        return document.documentElement.lang === 'ar' || document.documentElement.getAttribute('dir') === 'rtl';
      }

      _translateOptName(name) {
        const isAr = document.documentElement.lang === 'ar' || document.documentElement.getAttribute('dir') === 'rtl';
        if (!isAr) return name;
        const n = (name || '').toLowerCase().trim();
        if (n === 'size' || name === 'مقاس' || name === 'مقاسات' || name === 'المقياس') return 'المقاس';
        if (n === 'color' || n === 'colour' || name === 'لون' || name === 'ألوان') return 'اللون';
        return name;
      }

      _buildVariantHTML(p) {
        if (!p.variants || p.variants.length <= 1) return '';
        const options = this._buildOptions(p);
        if (!options.length || options.every(o => o.values.length <= 1)) return '';

        return `<div class="qv-options">
          ${options.map((opt, i) => {
            const displayName = this._translateOptName(opt.name);
            const isColor = this._isColorOpt(opt.name);
            const items = isColor
              ? opt.values.map((val, vi) => {
                  const bg = this._swatchColor(val);
                  return `<label class="qv-swatch${vi === 0 ? ' selected' : ''}" title="${this._esc(val)}" data-tooltip>
                    <input type="radio" name="qvopt${i}" value="${this._esc(val)}"
                           ${vi === 0 ? 'checked' : ''} class="qv-pill__input visually-hidden">
                    <span class="qv-swatch__dot" style="background-color:${this._esc(bg)}"></span>
                    <span class="tooltip">${this._esc(val)}</span>
                  </label>`;
                }).join('')
              : opt.values.map((val, vi) => `
                  <label class="qv-pill${vi === 0 ? ' selected' : ''}">
                    <input type="radio" name="qvopt${i}" value="${this._esc(val)}"
                           ${vi === 0 ? 'checked' : ''} class="qv-pill__input visually-hidden">
                    <span class="qv-pill__label">${this._esc(val)}</span>
                  </label>`).join('');

            const isSize = this._isSizeOpt(opt.name);
            const sizeGuideLink = isSize
              ? `<button type="button" class="qv-size-guide" onclick="(function(){var m=document.getElementById('SizeGuideModal');if(m&&m.showModal)m.showModal();else{window.location.href=window.location.pathname+'#size-guide';}})()">
                  ${document.documentElement.lang === 'ar' ? 'جدول المقاسات' : 'Size Guide'}
                </button>`
              : '';
            return `<div class="qv-option">
              <div class="qv-option__name-row">
                <p class="qv-option__name">
                  <strong>${this._esc(displayName)}</strong><span class="qv-option__selected" data-opt="${i}">: ${this._esc(opt.values[0])}</span>
                </p>
                ${sizeGuideLink}
              </div>
              <div class="${isColor ? 'qv-swatches' : 'qv-pills'}">${items}</div>
            </div>`;
          }).join('')}
        </div>`;
      }

      _bindVariants(p, body) {
        const vid      = body.querySelector('.qv-vid');
        const atcBtn   = body.querySelector('.qv-atc');
        const atcLabel = atcBtn && atcBtn.querySelector('.qv-atc__label');
        const options  = this._buildOptions(p);
        const map      = {};
        (p.variants || []).forEach(v => {
          map[options.map((_, i) => v[`option${i + 1}`]).join('|')] = v;
        });

        const sync = () => {
          const selected = options.map((_, i) => {
            const r = body.querySelector(`[name="qvopt${i}"]:checked`);
            return r ? r.value : '';
          });
          selected.forEach((val, i) => {
            const span = body.querySelector(`.qv-option__selected[data-opt="${i}"]`);
            if (span && val) span.textContent = `: ${val}`;
          });
          body.querySelectorAll('.qv-pill, .qv-swatch').forEach(el => {
            const inp = el.querySelector('.qv-pill__input');
            if (inp) el.classList.toggle('selected', inp.checked);
          });
          const v = map[selected.join('|')] || p.variants[0];
          if (vid)      vid.value       = v ? v.id : '';
          if (atcBtn)   atcBtn.disabled  = !v || !v.available;
          if (atcLabel) atcLabel.textContent = v && v.available ? 'Add to Cart' : 'Sold Out';
        };

        body.querySelectorAll('.qv-pill__input').forEach(r => r.addEventListener('change', sync));
      }

      /* ── Quantity ────────────────────────────────────────────────── */
      _bindQty(body) {
        const input = body.querySelector('.qv-qty__input');
        if (!input) return;
        body.querySelector('.qv-qty__minus').addEventListener('click', () => {
          input.value = Math.max(1, parseInt(input.value, 10) - 1);
        });
        body.querySelector('.qv-qty__plus').addEventListener('click', () => {
          input.value = parseInt(input.value, 10) + 1;
        });
      }

      /* ── Add to cart ────────────────────────────────────────────── */
      _bindForm(body) {
        const form    = body.querySelector('.qv-form');
        const atcBtn  = body.querySelector('.qv-atc');
        const spinner = atcBtn && atcBtn.querySelector('.qv-atc__spinner');
        const label   = atcBtn && atcBtn.querySelector('.qv-atc__label');
        if (!form) return;

        form.addEventListener('submit', e => {
          e.preventDefault();
          if (!atcBtn || atcBtn.disabled) return;
          atcBtn.disabled = true;
          if (spinner) spinner.hidden = false;
          if (label)   label.hidden   = true;

          fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
            .then(r => r.json())
            .then(() => {
              if (spinner) spinner.hidden = true;
              if (label)   label.hidden   = false;
              atcBtn.disabled = false;
              this.hide();
              fetch('/cart.js').then(r => r.json()).then(cart => {
                document.querySelectorAll('.cart-count-bubble span,[data-cart-count]')
                  .forEach(el => el.textContent = cart.item_count);
              });
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            })
            .catch(() => {
              if (spinner) spinner.hidden = true;
              if (label)   label.hidden   = false;
              atcBtn.disabled = false;
            });
        });
      }

      /* ── Buy it now ─────────────────────────────────────────────── */
      _bindBuyNow(body) {
        const btn     = body.querySelector('.qv-buy-now');
        const form    = body.querySelector('.qv-form');
        if (!btn || !form) return;

        const spinner = btn.querySelector('.qv-buy-now__spinner');
        const label   = btn.querySelector('.qv-buy-now__label');

        btn.addEventListener('click', () => {
          if (btn.disabled) return;
          btn.disabled = true;
          if (spinner) spinner.hidden = false;
          if (label)   label.hidden   = true;

          /* Add to cart then redirect to checkout */
          fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
            .then(() => { window.location.href = '/checkout'; })
            .catch(() => {
              btn.disabled = false;
              if (spinner) spinner.hidden = true;
              if (label)   label.hidden   = false;
            });
        });
      }
    }
  );
}
